import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import HomePage from "./pages/HomePage";
import PricingPage from "./pages/PricingPage";
import DashboardPage from "./pages/DashboardPage";
import SuccessPage from "./pages/SuccessPage";
import LoginPage from "./pages/LoginPage";
import ProjectsPage from "./pages/ProjectsPage";
import LibraryPage from "./pages/LibraryPage";
import WorkspacesPage from "./pages/WorkspacesPage";
import WorkspaceDetailPage from "./pages/WorkspaceDetailPage";
import BatchProcessingPage from "./pages/BatchProcessingPage";
import BatchDetailPage from "./pages/BatchDetailPage";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const location = useLocation();
  
  useEffect(() => {
    checkAuth();
  }, [location]);
  
  const checkAuth = async () => {
    // Check if there's a session_id in URL hash
    if (window.location.hash.includes("session_id=")) {
      const sessionId = window.location.hash.split("session_id=")[1];
      await processSession(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Check existing session
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchSubscription();
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  const processSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      // Set cookie
      document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7*24*60*60}; secure; samesite=none`;
      setUser(response.data);
      await fetchSubscription();
    } catch (error) {
      console.error("Session processing error:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };
  
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setSubscription(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };
  
  return (
    <AuthContext.Provider value={{ user, loading, subscription, logout, refreshSubscription: fetchSubscription }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/success" element={<ProtectedRoute><SuccessPage /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
          <Route path="/workspaces" element={<ProtectedRoute><WorkspacesPage /></ProtectedRoute>} />
          <Route path="/workspaces/:workspaceId" element={<ProtectedRoute><WorkspaceDetailPage /></ProtectedRoute>} />
          <Route path="/batch" element={<ProtectedRoute><BatchProcessingPage /></ProtectedRoute>} />
          <Route path="/batch/:jobId" element={<ProtectedRoute><BatchDetailPage /></ProtectedRoute>} />
        </Routes>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
